import { useCallback, useEffect, useRef, useState } from "react";
import { TFunction } from "i18next";
import {
  treatmentPlanService,
  TreatmentPlanResponse,
  TreatmentPlanAppointment,
} from "../../../services/treatmentPlanService";
import { cdtCodesService, CDTCode } from "../../../services/cdtCodesService";
import { buildAppointmentUpdateData } from "../../../utils/treatmentPlanAppointmentUtils";
import { saveTreatmentPlanUpdate } from "../../../utils/treatmentPlanSaveUtils";

type NotificationSeverity = "success" | "error";

interface UseTreatmentPlanPageDataParams {
  planId: string | undefined;
  getAppointments: () => TreatmentPlanAppointment[];
  syncAppointmentsFromPlan: (plan: TreatmentPlanResponse) => void;
  showNotification: (
    message: string,
    severity?: NotificationSeverity,
  ) => void;
  t: TFunction;
}

export function useTreatmentPlanPageData({
  planId,
  getAppointments,
  syncAppointmentsFromPlan,
  showNotification,
  t,
}: UseTreatmentPlanPageDataParams) {
  const [treatmentPlan, setTreatmentPlan] =
    useState<TreatmentPlanResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [cdtCodes, setCdtCodes] = useState<CDTCode[]>([]);

  const fetchInitiatedRef = useRef(false);

  const fetchTreatmentPlan = useCallback(
    async (force = false) => {
      if (!planId) return null;
      if (!force && fetchInitiatedRef.current) return null;
      fetchInitiatedRef.current = true;
      try {
        setLoading(true);
        setError(null);
        const [data, codes] = await Promise.all([
          treatmentPlanService.getById(planId),
          cdtCodesService.getCDTCodes({ activeOnly: true }),
        ]);
        setTreatmentPlan(data);
        setCdtCodes(codes);
        return data;
      } catch (err) {
        setError(t("treatmentPlan.failedToLoad"));
        console.error("Error fetching treatment plan:", err);
        return null;
      } finally {
        setLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [planId],
  );

  useEffect(() => {
    fetchInitiatedRef.current = false;
  }, [planId]);

  const applyPlanUpdate = useCallback(
    (updated: TreatmentPlanResponse) => {
      setTreatmentPlan(updated);
      syncAppointmentsFromPlan(updated);
    },
    [syncAppointmentsFromPlan],
  );

  const handleSaveHeaderDetails = useCallback(
    async ({ name, status }: { name: string; status: number }) => {
      if (!treatmentPlan || !planId) return;

      const isStatusChangeScenario =
        (treatmentPlan.status === 5 && status === 6) ||
        (treatmentPlan.status === 6 && status === 7);

      try {
        await saveTreatmentPlanUpdate(
          treatmentPlan.status,
          async (saveStatus, hasAppointmentUpdates) => {
            const updated = await treatmentPlanService.update(planId, {
              name,
              treatmentSummary: treatmentPlan.treatmentSummary,
              providerNote: treatmentPlan.providerNote,
              fee: treatmentPlan.fee || undefined,
              patientSummary: treatmentPlan.patientSummary || undefined,
              status: saveStatus,
              appointments: buildAppointmentUpdateData(getAppointments()),
              hasAppointmentUpdates,
            });
            applyPlanUpdate(updated);
            showNotification(t("treatmentPlan.changesSaved"));
          },
          {
            explicitStatus: isStatusChangeScenario ? status : undefined,
          },
        );
      } catch (err) {
        console.error("Error saving treatment plan header:", err);
        showNotification(t("editTreatmentPlan.failedToSave"), "error");
        throw err;
      }
    },
    [
      applyPlanUpdate,
      getAppointments,
      planId,
      showNotification,
      t,
      treatmentPlan,
    ],
  );

  const handleSaveTreatmentSummary = useCallback(
    async (treatmentSummary: string) => {
      if (!treatmentPlan || !planId) return;

      try {
        await saveTreatmentPlanUpdate(
          treatmentPlan.status,
          async (saveStatus, hasAppointmentUpdates) => {
            const updated = await treatmentPlanService.update(planId, {
              name: treatmentPlan.name,
              treatmentSummary,
              providerNote: treatmentPlan.providerNote,
              fee: treatmentPlan.fee || undefined,
              patientSummary: treatmentPlan.patientSummary || undefined,
              status: saveStatus,
              appointments: buildAppointmentUpdateData(getAppointments()),
              hasAppointmentUpdates,
            });
            applyPlanUpdate(updated);
            showNotification(t("treatmentPlan.changesSaved"));
          },
        );
      } catch (err) {
        console.error("Error saving treatment summary:", err);
        showNotification(t("editTreatmentPlan.failedToSave"), "error");
        throw err;
      }
    },
    [
      applyPlanUpdate,
      getAppointments,
      planId,
      showNotification,
      t,
      treatmentPlan,
    ],
  );

  const handleSaveProviderNote = useCallback(
    async (providerNote: string) => {
      if (!treatmentPlan || !planId) return;

      try {
        await saveTreatmentPlanUpdate(
          treatmentPlan.status,
          async (saveStatus, hasAppointmentUpdates) => {
            const updated = await treatmentPlanService.update(planId, {
              name: treatmentPlan.name,
              treatmentSummary: treatmentPlan.treatmentSummary,
              providerNote: providerNote || undefined,
              fee: treatmentPlan.fee || undefined,
              patientSummary: treatmentPlan.patientSummary || undefined,
              status: saveStatus,
              appointments: buildAppointmentUpdateData(getAppointments()),
              hasAppointmentUpdates,
            });
            applyPlanUpdate(updated);
            showNotification(t("treatmentPlan.changesSaved"));
          },
        );
      } catch (err) {
        console.error("Error saving provider note:", err);
        showNotification(t("editTreatmentPlan.failedToSave"), "error");
        throw err;
      }
    },
    [
      applyPlanUpdate,
      getAppointments,
      planId,
      showNotification,
      t,
      treatmentPlan,
    ],
  );

  const handleSavePatientSummary = useCallback(
    async (content: string) => {
      if (!treatmentPlan || !planId) throw new Error("No treatment plan");
      try {
        const updated = await treatmentPlanService.update(planId, {
          name: treatmentPlan.name,
          treatmentSummary: treatmentPlan.treatmentSummary,
          providerNote: treatmentPlan.providerNote,
          fee: treatmentPlan.fee || undefined,
          patientSummary: content,
          status: treatmentPlan.status,
          appointments: buildAppointmentUpdateData(getAppointments()),
        });
        applyPlanUpdate(updated);
        showNotification(t("treatmentPlan.changesSaved"));
      } catch (err) {
        console.error("Error saving patient summary:", err);
        showNotification(t("treatmentPlan.summarizedViewSaveFailed"), "error");
        throw err;
      }
    },
    [
      applyPlanUpdate,
      getAppointments,
      planId,
      showNotification,
      t,
      treatmentPlan,
    ],
  );

  return {
    treatmentPlan,
    setTreatmentPlan,
    loading,
    error,
    cdtCodes,
    fetchTreatmentPlan,
    handleSaveHeaderDetails,
    handleSaveTreatmentSummary,
    handleSaveProviderNote,
    handleSavePatientSummary,
  };
}
